/**
 * sceneDirtyState.ts
 *
 * "Does the open scene hold edits that a save would write?" — asked by anything
 * that is about to navigate away from the editor (the Header logo's shortcut
 * back to the project's scene list).
 *
 * The answer is not "the undo stack is non-empty": the stack keeps its commands
 * after a save, and undoing back to where a save happened leaves nothing to
 * lose. So the save point is marked IN the history (useHistoryStore.markSaved(),
 * called by SceneView after a successful save) and the stack reports whether it
 * has moved away from that mark.
 *
 * The stack alone still isn't the whole answer. Two kinds of edit change what a
 * save writes without ever producing an undo command, and SceneView's saveScene
 * already treats both as reasons to write:
 *
 *  - floorplan geometry drawn in plan mode (Blueprint3D's own edit gestures set
 *    the flag behind getFloorplanSaveState().isDirty());
 *  - wall-face material splits (useSurfaceSplitStore.isDirty), which change the
 *    architecture mesh and force a GLB re-export.
 *
 * Keep this list in step with the dirty flags saveScene consults — a source it
 * saves but this doesn't is a source the user can lose without being warned.
 */

import { useHistoryStore } from '../stores/useHistoryStore';
import { useSurfaceSplitStore } from '../stores/useSurfaceSplitStore';
import { getFloorplanSaveState } from './floorplanSaveBridge';

export function hasUnsavedSceneChanges(): boolean {
  if (useHistoryStore.getState().isDirty) return true;
  if (useSurfaceSplitStore.getState().isDirty) return true;
  // null while no floorplan editor is initialised — nothing to be dirty about.
  if (getFloorplanSaveState()?.isDirty()) return true;
  return false;
}
